import PageBanner from 'LuxKit/Banners/PageBanner'
import TextLink from 'LuxKit/TextLink'
import { agentHubOrSubBrandConfig } from 'agentHub/constants'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import config from 'constants/config'
import { LabFeatureFlags } from 'constants/lab'
import GeoContext from 'contexts/geoContext'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import useIsSearchPage from 'hooks/Navigation/useIsSearchPage'
import useIsHomePage from 'hooks/Navigation/useIsHomePage'
import { useAppSelector } from 'hooks/reduxHooks'
import getCanLoyaltyEnrollExistingUser from 'loyalty/selectors/getCanLoyaltyEnrollExistingUser'
import { selectLoggedIn } from 'selectors/accountSelectors'
import LuxPlusRenewalBanner from 'luxPlus/components/Banners/LuxPlusRenewalBanner'
import LuxPlusWarningMessageBanner from 'luxPlus/components/Banners/LuxPlusWarningMessageBanner'
import LuxPlusFlightVoucherOfferBanner from 'luxPlus/components/Banners/LuxPlusFlightVoucherOfferBanner'
import { getHasLuxPlusPendingRenewalBanner } from 'luxPlus/selectors/featureToggle'
import { useContext, useMemo, useRef } from 'react'
import { FormattedMessage } from 'react-intl'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import AtolProtectedBanner from './HeaderBanners/AtolProtectedBanner'
import CookiesDisabledWarningBanner from './HeaderBanners/CookiesDisabledWarningBanner'
import NotificationBannerV3 from './HeaderBanners/NotificationBannerV3'
import SiteTakeoverPageBanner from 'takeovers/utils/components/SiteTakeoverPageBanner'
import SiteTakeoverPopup from 'takeovers/utils/components/SiteTakeoverPopup'
import { useShouldShowBanner } from 'takeovers/utils/hooks/useShouldShowBanner'
import { isLabTakeoverBannerContentPayload } from 'lib/lab/dynamicPayload'
import LoyaltyMembershipActivationPageBanner from 'loyalty/components/Banners/LoyaltyMembershipActivationPageBanner'
import getTasteOfGoldPromoCampaign from 'takeovers/tasteOfGoldTakeover/selectors/getTasteOfGoldPromoCampaign'
import useAgentHubSocieteLoginPrompt from 'agentHub/hooks/useAgentHubSocieteLoginPrompt'

/**
 * Contains all the banners that are meant to be displayed on top of the page.
 */
function HeaderTopBanners() {
  const { currentRegionCode } = useContext(GeoContext)
  const containerRef = useRef<HTMLDivElement>(null)

  const canLoyaltyEnrollExistingUser = useAppSelector(getCanLoyaltyEnrollExistingUser)
  const subscription = useAppSelector(state => state.auth.account.luxPlus.member.subscription)
  const hasLuxPlusPendingRenewalBanner = useAppSelector(getHasLuxPlusPendingRenewalBanner)
  const agentHubPhoneNumber = useAppSelector(state => state.geo.currentContact?.local.humanReadable ?? '')
  const isLoggedIn = useAppSelector(selectLoggedIn)

  const isTasteOfGoldActive = !!useAppSelector(getTasteOfGoldPromoCampaign)
  const enableTakeoverBannerContent = useLabFeatureFlag(LabFeatureFlags.enableTakeoverBannerContent)
  const isSearchPage = useIsSearchPage()
  const isHomePage = useIsHomePage()
  const shouldShowGenericBanner = useShouldShowBanner({ location: 'generic' })

  const bannerContentPayload: App.LabTakeoverBannerContentPayload | undefined = useMemo(() => {
    if (enableTakeoverBannerContent?.enabled) {
      const payload = enableTakeoverBannerContent.value
      if (isLabTakeoverBannerContentPayload(payload)) {
        return payload
      }
    }
    return undefined
  }, [enableTakeoverBannerContent])

  useAgentHubSocieteLoginPrompt()

  return <div ref={containerRef} className="no-print">
    <NotificationBannerV3 />
    {subscription.error && <LuxPlusWarningMessageBanner />}
    {hasLuxPlusPendingRenewalBanner && <LuxPlusRenewalBanner />}
    {canLoyaltyEnrollExistingUser && !isTasteOfGoldActive && <LoyaltyMembershipActivationPageBanner />}
    {currentRegionCode === 'GB' && <TripGuard>
      <CSSBreakpoint max="mobile">
        <AtolProtectedBanner />
      </CSSBreakpoint>
    </TripGuard>}
    {config.agentHub.isEnabled && (
      <>
        {/* TODO remove this banner once middle east air space is cleared https://aussiecommerce.atlassian.net/browse/LEAH-1451 */}
        {isLoggedIn && (
          <PageBanner kind="warning" align="center">
            <FormattedMessage
              id="rBuRvway7Hef5Deqc2YU"
              defaultMessage="We are monitoring the evolving situation in the Middle East. If any of your upcoming bookings may be affected, please contact our team on {phoneNumber} for support."
              description="Agent hub Middle East situation warning banner in page header"
              values={{
                phoneNumber: agentHubPhoneNumber,
              }}
            />
          </PageBanner>
        )}
        <PageBanner kind="promotional-primary" align="center">
          <FormattedMessage
            id="dWKLR3ckHKyE3edGxGcI"
            defaultMessage="Need help? Email <emailLink>{supportEmail}</emailLink>"
            description="Agent hub support email banner in the page header in the site header"
            values={{
              supportEmail: agentHubOrSubBrandConfig.supportEmail,
              emailLink: (chunks) => <TextLink href={`mailto:${agentHubOrSubBrandConfig.supportEmail}`}>{chunks}</TextLink>,
            }}
          />
        </PageBanner>
      </>
    )}
    <CookiesDisabledWarningBanner />
    {!isHomePage && !isSearchPage && shouldShowGenericBanner && (
      bannerContentPayload?.genericBannerType === 'takeoverpopup' || bannerContentPayload?.genericBannerType === 'whitepopup'
        ? <SiteTakeoverPopup />
        : <SiteTakeoverPageBanner />
    )}
    {(!isTasteOfGoldActive || isHomePage) && <LuxPlusFlightVoucherOfferBanner />}
  </div>
}

export default HeaderTopBanners
